import { useTransaction } from "hooks/useTransactions";
import { isEmpty } from "lodash";
import InfoTag from "pages/echeck/components/InfoTag";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader } from "pages/_components/TransactionCard";
import React, { useState } from "react";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { actions as transferActions } from "reducers/transfers";
import * as i18n from "util/i18n";
import gaEventTracker, { events } from "util/gaEventTracker";
import { isIOS } from "react-device-detect";
import InternalTransferSummaryBody from "./components/Summary";

const InternalTransferTransactionPage = ({ isDesktop }) => {
    const { state = {} } = useLocation();
    const { isFlowComplete = false } = state;

    const formatTransactionTransfer = (data, idTransaction) => ({ ...data, idTransaction });
    const confirmRoute = `/transfers/internal/confirm`;

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        userId,
        status,
    } = useTransaction(
        confirmRoute,
        transferActions.setInternalTransferData,
        transferActions.cleanTransferTicket,
        formatTransactionTransfer,
    );

    const { isFinished, isPending, isScheduled, isBackoffice, isCancelled } = status;
    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint(
        "transfer.internal.summary.title",
        id,
        "InternalTransferTransaction",
    );

    const backLinkTo = isFlowComplete ? "/transfers" : "";
    const isProgrammed = !isEmpty(transaction?.data?.scheduler);
    const [showMoreData, setShowMoreData] = useState(!isDesktop);

    const cancelTransaction = () => {
        const { data, idTransaction } = transaction;

        dispatch(
            transferActions.setInternalTransferData({
                ...data,
                idTransaction,
                cancelTransaction: true,
                userId,
            }),
        );
        dispatch(push(confirmRoute));
    };

    const getMdOffset = () => {
        if (!!showSignButton && !!isScheduled) {
            return 0;
        }
        if (!!isFinished || !!showSignButton || !!isScheduled) {
            return 2;
        }
        return 4;
    };

    const handleSign = () => {
        gaEventTracker.triggerEvent(events.trans_mis_cuentas_firmar);
        return handleSignTransaction();
    };

    const handleDownload = async () => {
        await setShowMoreData(true);
        gaEventTracker.triggerEvent(events.trans_mis_cuentas_descargar());
        return handlePrint();
    };

    // render de los botones
    const renderBackToHomeButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset} className={isIOS && !isPrimary ? "mt-9" : null}>
            <Button
                onClick={() => dispatch(push("/desktop"))}
                label="forms.transaction.ticket.actions.backToHome"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderDownloadButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset} className={isIOS && !isPrimary ? "mt-9" : null}>
            <Button
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
                image="images/figma/icon/download.svg"
                label="global.download"
                onClick={handleDownload}
            />
        </Col>
    );

    const renderSignButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset} className={isIOS && !isPrimary ? "mt-9" : null}>
            <Button
                onClick={handleSign}
                label="forms.signTransaction.link"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    const renderCancelButton = (isPrimary, offset) => (
        <Col xs={12} md={4} mdOffset={offset} className={isIOS && !isPrimary ? "mt-9" : null}>
            <Button
                onClick={cancelTransaction}
                label="lotPayment.cancel"
                className={`btn${!isPrimary ? " btn-outline" : " btn-primary"}`}
            />
        </Col>
    );

    return (
        <>
            <Notification scopeToShow="InternalTransferTransaction" />
            {!isBackoffice && (
                <ResponsiveHeader ticket metaTitle="transfer.internal.summary.title" backLinkTo={backLinkTo} />
            )}
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="transfer.internal.summary.title"
                                subTitle={isProgrammed && isPending ? `ticket.status.PENDING.schedule` : ""}
                                infoComponents={
                                    isProgrammed ? (
                                        <InfoTag tagBackground="programmed" message={i18n.get("scheduler.tag")} />
                                    ) : null
                                }
                            />
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <InternalTransferSummaryBody
                                        ticket
                                        isDesktop={isDesktop}
                                        setShowMoreData={setShowMoreData}
                                        showMoreData={showMoreData}
                                    />
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    {!isBackoffice && (
                        <Row className="form-actions no-print">
                            {isCancelled && renderBackToHomeButton(false, 4)}
                            {isScheduled && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderCancelButton(true, 0)}
                                </>
                            )}
                            {isFinished && (
                                <>
                                    {renderBackToHomeButton(false, 2)}
                                    {renderDownloadButton(true, 0)}
                                </>
                            )}

                            {isPending &&
                                (showSignButton ? (
                                    <>
                                        {renderCancelButton(false, 2)}
                                        {renderSignButton(true, 0)}
                                    </>
                                ) : (
                                    <>
                                        {renderBackToHomeButton(false, 2)}
                                        {renderCancelButton(true, 0)}
                                    </>
                                ))}

                            {isFinished && !isDesktop && (
                                <Col xs={12} md={4}>
                                    <Button
                                        className="btn btn-outline"
                                        image="images/figma/icon/share.svg"
                                        label="global.share"
                                        onClick={() => {
                                            handleShareMobile();
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}
                </div>
            </MainContainer>
        </>
    );
};

InternalTransferTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default InternalTransferTransactionPage;
